import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const TOKEN_KEY = "swingtrader_token";

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
});

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);
export const isAuthenticated = () => !!getToken();

// Attach the auth token to every request
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercept errors and normalize them; log out on an expired/invalid token
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const isLoginRequest = err?.config?.url?.includes("/api/auth/login");
    if (err?.response?.status === 401 && !isLoginRequest) {
      clearToken();
      window.dispatchEvent(new Event("auth:unauthorized"));
    }
    const message =
      err?.response?.data?.detail ||
      err?.message ||
      "An unexpected error occurred";
    return Promise.reject(new Error(message));
  }
);

// ── Auth ─────────────────────────────────────────────────────────────────────

export const login = (username, password) =>
  api.post("/api/auth/login", { username, password }).then((r) => {
    if (r.data.access_token) setToken(r.data.access_token);
    return r.data;
  });

export const googleLogin = (credential) =>
  api.post("/api/auth/google", { credential }).then((r) => {
    if (r.data.access_token) setToken(r.data.access_token);
    return r.data;
  });

export const register = (username, password, email, fullName) =>
  api.post("/api/auth/register", { username, password, email, full_name: fullName || undefined }).then((r) => {
    if (r.data.access_token) setToken(r.data.access_token);
    return r.data;
  });

export const restoreAccount = (username, password) =>
  api.post("/api/auth/restore", { username, password }).then((r) => {
    if (r.data.access_token) setToken(r.data.access_token);
    return r.data;
  });

export const restoreGoogleAccount = (credential) =>
  api.post("/api/auth/google/restore", { credential }).then((r) => {
    if (r.data.access_token) setToken(r.data.access_token);
    return r.data;
  });

export const deleteMyAccount = () => api.post("/api/auth/delete-account").then((r) => r.data);

export const logout = () => {
  clearToken();
  localStorage.removeItem("swingtrader_admin_token");
  window.dispatchEvent(new Event("auth:unauthorized"));
};

export const getMyProfile = () => api.get("/api/auth/me").then((r) => r.data);

export const updateMyProfile = (profile) => api.put("/api/auth/me", profile).then((r) => r.data);

export const updateMyTheme = (theme) => api.put("/api/auth/me/theme", { theme }).then((r) => r.data);

export const getRobinhoodStatus = () => api.get("/api/robinhood/status").then((r) => r.data);

// ── Admin ────────────────────────────────────────────────────────────────────

export const getAdminStats = () => api.get("/api/admin/stats").then((r) => r.data);

export const getAdminUsers = (q) =>
  api.get("/api/admin/users", { params: q ? { q } : {} }).then((r) => r.data);

export const freezeUser = (userId) =>
  api.post(`/api/admin/users/${userId}/freeze`).then((r) => r.data);

export const unfreezeUser = (userId) =>
  api.post(`/api/admin/users/${userId}/unfreeze`).then((r) => r.data);

export const adminDeleteUser = (userId) =>
  api.post(`/api/admin/users/${userId}/delete`).then((r) => r.data);

export const adminRestoreUser = (userId) =>
  api.post(`/api/admin/users/${userId}/restore`).then((r) => r.data);

export const getAdminAlerts = ({ q, userId, offset = 0 } = {}) =>
  api.get("/api/admin/alerts", {
    params: { ...(q && { q }), ...(userId && { user_id: userId }), offset },
  }).then((r) => r.data);

// Impersonation: stash the admin's own token before switching to the
// target user's, so "exit impersonation" can restore it without a re-login.
const ADMIN_TOKEN_KEY = "swingtrader_admin_token";

export const startImpersonation = (userId) =>
  api.post(`/api/admin/users/${userId}/impersonate`).then((r) => {
    const adminToken = getToken();
    if (adminToken) localStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
    setToken(r.data.access_token);
    return r.data;
  });

export const isImpersonating = () => !!localStorage.getItem(ADMIN_TOKEN_KEY);

export const exitImpersonation = () => {
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  if (adminToken) {
    setToken(adminToken);
  } else {
    clearToken();
  }
};

// ── Stock ────────────────────────────────────────────────────────────────────

export const getStockSummary = (ticker) =>
  api.get(`/api/stock/${ticker}/summary`, { timeout: 20000 }).then((r) => r.data);

export const getStockHistory = (ticker, period = "3mo") =>
  api.get(`/api/stock/${ticker}`, { params: { period } }).then((r) => r.data);

export const getStockIndicators = (ticker, period = "6mo", customRange = null) =>
  api
    .get(`/api/stock/${ticker}/indicators`, { params: { period, ...(customRange && { start_date: customRange.start, end_date: customRange.end }) } })
    .then((r) => r.data);

export const getStockFinancials = (ticker) =>
  api.get(`/api/stock/${ticker}/financials`, { timeout: 30000 }).then((r) => r.data);

export const getStockStats = (ticker) =>
  api.get(`/api/stock/${ticker}/stats`, { timeout: 15000 }).then((r) => r.data);

export const getFundamentalScorecard = (ticker) =>
  api.get(`/api/stock/${ticker}/scorecard`, { timeout: 20000 }).then((r) => r.data);

export const getTickerSupervisor = (ticker) =>
  api.get(`/api/stock/${ticker}/supervisor`, { timeout: 90000 }).then((r) => r.data);

export const getStockNews = (ticker, limit = 10) =>
  api.get(`/api/stock/${ticker}/news`, { params: { limit }, timeout: 15000 }).then((r) => r.data);

export const getStockRatings = (ticker, limit = 8) =>
  api.get(`/api/stock/${ticker}/ratings`, { params: { limit }, timeout: 15000 }).then((r) => r.data);

export const getStockInsiders = (ticker, limit = 10) =>
  api.get(`/api/stock/${ticker}/insiders`, { params: { limit }, timeout: 15000 }).then((r) => r.data);

export const getMarketNews = (limit = 15) =>
  api.get("/api/market/news", { params: { limit }, timeout: 20000 }).then((r) => r.data);

export const simulateInvestment = (ticker, amount, { period, startDate } = {}) =>
  api.get(`/api/stock/${ticker}/simulate`, {
    params: { amount, ...(period && { period }), ...(startDate && { start_date: startDate }) },
    timeout: 30000,
  }).then((r) => r.data);

// ── Screener ─────────────────────────────────────────────────────────────────

export const runScreener = () =>
  api.get("/api/screener").then((r) => r.data);

export const getScreenerTickers = () =>
  api.get("/api/screener/tickers").then((r) => r.data);

export const updateScreenerTickers = (ticker, action) =>
  api.post("/api/screener/tickers", { ticker, action }).then((r) => r.data);

// ── Market overview ───────────────────────────────────────────────────────────

export const getMarketOverview = () =>
  api.get("/api/market").then((r) => r.data);

export const getMarketCapBreakdown = () =>
  api.get("/api/market/caps", { timeout: 120000 }).then((r) => r.data);

// ── Portfolio ─────────────────────────────────────────────────────────────────

export const getPortfolio = () =>
  api.get("/api/portfolio").then((r) => r.data);

export const addTrade = (trade) =>
  api.post("/api/portfolio/trade", trade).then((r) => r.data);

export const deleteTrade = (tradeId) =>
  api.delete(`/api/portfolio/trade/${tradeId}`).then((r) => r.data);

export const analyzePortfolio = (period = "6mo") =>
  api.get("/api/portfolio/analyze", { params: { period }, timeout: 120000 }).then((r) => r.data);

export const getPortfolioJournal = () =>
  api.get("/api/portfolio/journal").then((r) => r.data);

export const syncRobinhoodPortfolio = () =>
  api.post("/api/portfolio/sync-robinhood", {}, { timeout: 30000 }).then((r) => r.data);

export const getWallet = () => api.get("/api/wallet").then((r) => r.data);

export const addWalletTransaction = (type, amount, note) =>
  api.post("/api/wallet/transaction", { type, amount, note }).then((r) => r.data);

// ── Chips ─────────────────────────────────────────────────────────────────────

export const getChipStocks = (period = "1mo", customRange = null) =>
  api.get("/api/chips", { params: { period, ...(customRange && { start_date: customRange.start, end_date: customRange.end }) } }).then((r) => r.data);

export const analyzeChipStocks = (period = "6mo") =>
  api.get("/api/chips/analyze", { params: { period }, timeout: 180000 }).then((r) => r.data);

export const getTopChipPicks = (period = "6mo", limit = 3) =>
  api.get("/api/chips/top", { params: { period, limit }, timeout: 180000 }).then((r) => r.data);

// ── Global Scan ───────────────────────────────────────────────────────────────

export const getScanFilters = () =>
  api.get("/api/scan/filters").then((r) => r.data);

export const runGlobalScan = (filters, period = "6mo") =>
  api.post("/api/scan/run", { filters, period }, { timeout: 180000 }).then((r) => r.data);

// ── Daily Movers ──────────────────────────────────────────────────────────────

export const getDailyMovers = () =>
  api.get("/api/movers", { timeout: 120000 }).then((r) => r.data);

// ── ETFs & Index Funds ────────────────────────────────────────────────────────

export const getETFs = (period = "1mo", customRange = null) =>
  api.get("/api/etfs", { params: { period, ...(customRange && { start_date: customRange.start, end_date: customRange.end }) } }).then((r) => r.data);

export const analyzeETFs = (period = "6mo") =>
  api.get("/api/etfs/analyze", { params: { period }, timeout: 180000 }).then((r) => r.data);

export const getTopETFPicks = (period = "6mo", limit = 3) =>
  api.get("/api/etfs/top", { params: { period, limit }, timeout: 180000 }).then((r) => r.data);

// ── Top Stocks ────────────────────────────────────────────────────────────────

export const getTopStocks = (period = "1mo", sector = null, customRange = null) =>
  api.get("/api/topstocks", { params: { period, ...(sector && { sector }), ...(customRange && { start_date: customRange.start, end_date: customRange.end }) } }).then((r) => r.data);

export const analyzeTopStocks = (period = "6mo", sector = null) =>
  api.get("/api/topstocks/analyze", { params: { period, ...(sector && { sector }) }, timeout: 300000 }).then((r) => r.data);

export const getTopStockPicks = (period = "6mo", limit = 3, sector = null) =>
  api.get("/api/topstocks/top", { params: { period, limit, ...(sector && { sector }) }, timeout: 300000 }).then((r) => r.data);

// ── Generic Sectors ───────────────────────────────────────────────────────────

export const listSectors = () =>
  api.get("/api/sectors").then((r) => r.data);

export const getSectorStocks = (sectorId, period = "1mo", customRange = null) =>
  api.get(`/api/sector/${sectorId}`, { params: { period, ...(customRange && { start_date: customRange.start, end_date: customRange.end }) } }).then((r) => r.data);

export const analyzeSector = (sectorId, period = "6mo") =>
  api.get(`/api/sector/${sectorId}/analyze`, { params: { period }, timeout: 300000 }).then((r) => r.data);

export const getSectorTopPicks = (sectorId, period = "6mo", limit = 3) =>
  api.get(`/api/sector/${sectorId}/top`, { params: { period, limit }, timeout: 300000 }).then((r) => r.data);

// ── Watchlist ─────────────────────────────────────────────────────────────────

export const getWatchlist = () =>
  api.get("/api/watchlist").then((r) => r.data);

export const getWatchlistLists = (ticker) =>
  api.get("/api/watchlist/lists", { params: ticker ? { ticker } : {} }).then((r) => r.data);

export const updateWatchlist = (ticker, action, category = "Watchlist") =>
  api.post("/api/watchlist", { ticker, action, category }).then((r) => r.data);

export const analyzeWatchlist = (period = "6mo") =>
  api.get("/api/watchlist/analyze", { params: { period }, timeout: 300000 }).then((r) => r.data);

// ── Signals ───────────────────────────────────────────────────────────────────

export const getSignals = (limit = 5) =>
  api.get("/api/signals", { params: { limit } }).then((r) => r.data);

// ── Ticker search ────────────────────────────────────────────────────────────

export const searchTickers = (q, limit = 8) =>
  api.get("/api/tickers/search", { params: { q, limit }, timeout: 10000 }).then((r) => r.data);

// ── Stock Monitors ───────────────────────────────────────────────────────────

export const getMonitors = () =>
  api.get("/api/monitors").then((r) => r.data);

export const createMonitor = (ticker, criteria, options = {}) =>
  api.post("/api/monitors", {
    ticker,
    criteria,
    frequency_minutes: options.frequencyMinutes || undefined,
    market_hours_only: !!options.marketHoursOnly,
    expires_in_days: options.expiresInDays || undefined,
  }).then((r) => r.data);

export const deleteMonitor = (monitorId) =>
  api.delete(`/api/monitors/${monitorId}`).then((r) => r.data);

export const setCriterionActive = (criteriaId, active) =>
  api.patch(`/api/monitors/criteria/${criteriaId}`, { active }).then((r) => r.data);

export const connectWhatsApp = (phone) =>
  api.post("/api/monitors/whatsapp/connect", { phone }).then((r) => r.data);

export const getWhatsAppStatus = () =>
  api.get("/api/monitors/whatsapp/status").then((r) => r.data);

export const disconnectWhatsApp = () =>
  api.post("/api/monitors/whatsapp/disconnect").then((r) => r.data);

export const getEmailAlertStatus = () =>
  api.get("/api/monitors/email/status").then((r) => r.data);

export const setEmailAlertsEnabled = (enabled) =>
  api.patch("/api/monitors/email/status", { enabled }).then((r) => r.data);

export const sendTestEmailAlert = () =>
  api.post("/api/monitors/email/test").then((r) => r.data);

// ── Reports ──────────────────────────────────────────────────────────────────

export const getReports = () =>
  api.get("/api/reports").then((r) => r.data);

export const createReport = (report) =>
  api.post("/api/reports", report).then((r) => r.data);

export const updateReport = (reportId, report) =>
  api.put(`/api/reports/${reportId}`, report).then((r) => r.data);

export const deleteReport = (reportId) =>
  api.delete(`/api/reports/${reportId}`).then((r) => r.data);

// Report generation (multiple tickers analyzed + emailed) can take a while
// — a report mirroring Top Stocks' Analyze button re-analyzes all 40
// tickers with no threading, same as that page's own "Analyze" button, so
// this matches analyzeWatchlist's existing 300s precedent rather than the
// app's more typical 120s ceiling.
export const runReportNow = (reportId) =>
  api.post(`/api/reports/${reportId}/run`, {}, { timeout: 300000 }).then((r) => r.data);
